import { useState } from 'react';
import type { Cellar } from '../domain/cellar';
import { buildCellarCsv, exportFileName } from '../domain/csv-export';
import { t } from '../i18n';

/**
 * "Exporter ma cave en CSV" (T097, spec.md User Story 11, contracts/csv-export.md): builds the file
 * in the browser and hands it to the phone as a download named after the cellar's person and the
 * month. An empty cellar still gives the header line, and the person is told it is empty.
 */
export function ExportCellarButton({
  label,
  load,
}: {
  label: string;
  /**
   * The cellar to export (undefined when the person has none yet) and its person's name or email,
   * for the file name.
   */
  load: () => Promise<{ cellar: Cellar | undefined; name: string }>;
}) {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | undefined>();

  async function exportCsv() {
    setBusy(true);
    setMessage(undefined);
    try {
      const loaded = await load();
      const cellar = loaded.cellar ?? { schemaVersion: 0, wines: [] };
      const url = URL.createObjectURL(
        new Blob([buildCellarCsv(cellar)], { type: 'text/csv;charset=utf-8' }),
      );
      const link = document.createElement('a');
      link.href = url;
      link.download = exportFileName(loaded.name);
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      if (cellar.wines.length === 0) setMessage(t('export.empty'));
    } catch {
      setMessage(t('export.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="export-cellar">
      <button type="button" className="cta-action" disabled={busy} onClick={() => void exportCsv()}>
        {label}
      </button>
      {message && <p role="status">{message}</p>}
    </div>
  );
}
